
import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { AddEditCampaign } from "./add-edit-Compaign";
import VoterReachOutCampaignOverview from "./voter-reachoutCompaignoverview";
import VoterReachOutListCampaign from "./voter-reachoutListCompaign";
import { useStore } from "../../../helpers/helpers";
import { useParams } from "react-router";
import { DeleteModal, SideOverlay } from "../../../components";
import { MegaphoneIcon, PlusIcon } from "@heroicons/react/24/outline";
import VoterReachOutSectionHeader from "../voter-reachout-section-header";

interface VoterReachOutCampaignsProps {
  projectid?: string;
}

export const VoterReachOutCampaigns = observer(
  (props: VoterReachOutCampaignsProps) => {
    const { projectid: propProjectId } = props;
    const { projectid: paramProjectId } =
      useParams<{ projectid: string }>();

    const projectid = propProjectId || paramProjectId;

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteItem, setDeleteItem] = useState<string | null>(null);
    const [popOver, setPopOver] = useState(false);

    const { voterReachOutStore } = useStore();

    const {
      addCampaign,
      editCampaign,
      isMessageOverviewOpen,
      selectedOverViewItem,
    } = voterReachOutStore;

    const sideOverlayTitle =
      selectedOverViewItem?.name || "Campaign Details";

    const getReachOutCampaign = useCallback(() => {
      if (!projectid) return;

      voterReachOutStore.get_reachout_Campaign({
        projectid,
        query: "",
      });
    }, [projectid, voterReachOutStore]);

    const getReachOutTarget = useCallback(() => {
      if (!projectid) return;

      voterReachOutStore.get_reachout_target({
        projectid,
        query: "",
      });
    }, [projectid, voterReachOutStore]);

    const getReachOutMessages = useCallback(() => {
      if (!projectid) return;

      voterReachOutStore.get_reachout_message({
        projectid,
        query: "",
        channel: "",
        offset: 0,
        count: 100,
      });
    }, [projectid, voterReachOutStore]);

    useEffect(() => {
      getReachOutCampaign();
      getReachOutMessages();
      getReachOutTarget();
    }, [
      getReachOutCampaign,
      getReachOutMessages,
      getReachOutTarget,
      selectedOverViewItem,
    ]);

    const handleOverlayClose = useCallback(() => {
      voterReachOutStore.setAddCampaignStatus(false);
      voterReachOutStore.set_formType("new");
    }, [voterReachOutStore]);

    const handleCreateCampaign = useCallback(() => {
      voterReachOutStore.setAddCampaignStatus(true);
      voterReachOutStore.setEditCampaignStatus(false);
    }, [voterReachOutStore]);

    const deleteHandler = useCallback(async () => {
      if (!deleteItem) return;

      try {
        const result =
          await voterReachOutStore.delete_reachout_campaign({
            id: deleteItem,
          });

        if (result.status === 200) {
          toast.success("Campaign deleted successfully");

          if (
            voterReachOutStore.selectedOverViewItem?.id === deleteItem
          ) {
            voterReachOutStore.set_IsMessageOverviewOpen(false);
            voterReachOutStore.set_selected_overview_item(null);
          }

          voterReachOutStore.reachOutCampaignList =
            voterReachOutStore.reachOutCampaignList.filter(
              (item: any) => item.id !== deleteItem
            );

          voterReachOutStore.lastReachOutCampaignPayload = null;

          getReachOutCampaign();
        } else {
          toast.error("Failed to delete campaign");
        }
      } catch {
        toast.error(
          "An error occurred while deleting the campaign"
        );
      } finally {
        setIsDeleteModalOpen(false);
        setDeleteItem(null);
      }
    }, [deleteItem, voterReachOutStore, getReachOutCampaign]);

    const onDeleteHandler = useCallback((itemId: string) => {
      setIsDeleteModalOpen(true);
      setDeleteItem(itemId);
    }, []);

    const handlePopOverState = useCallback((bool: boolean) => {
      setPopOver(bool);
    }, []);

    const handleCampaignOverviewClose = useCallback(() => {
      voterReachOutStore.set_IsMessageOverviewOpen(false);
    }, [voterReachOutStore]);

    if (!projectid) {
      return (
        <div className="py-8 text-center text-gray-500">
          Project ID is required
        </div>
      );
    }

    return (
      <div>
        <div className="mt-4 space-y-4">
          <VoterReachOutSectionHeader
            title="Campaigns"
            description="Create and monitor your voter outreach campaigns."
            icon={MegaphoneIcon}
            action={
              <button
                type="button"
                onClick={handleCreateCampaign}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-200/60 transition-all hover:-translate-y-0.5 hover:from-indigo-700 hover:to-violet-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                <span>Create Campaign</span>
              </button>
            }
          />
          <VoterReachOutListCampaign
            setPopOver={handlePopOverState}
            deleteMessage={onDeleteHandler}
          />
        </div>

        <SideOverlay
          show={addCampaign}
          onClose={handleOverlayClose}
          title={editCampaign ? "Edit Campaign" : "Create Campaign"}
        >
          <AddEditCampaign
            projectId={projectid}
            getReachOutCampaign={getReachOutCampaign}
          />
        </SideOverlay>

        <SideOverlay
          show={isMessageOverviewOpen}
          onClose={handleCampaignOverviewClose}
          title={sideOverlayTitle}
        >
          <VoterReachOutCampaignOverview
            data={selectedOverViewItem}
          />
        </SideOverlay>

        {isDeleteModalOpen && (
          <DeleteModal
            isOpen={isDeleteModalOpen}
            desc="Are you sure you want to delete this campaign? This action cannot be undone."
            onDeleteClick={deleteHandler}
            onCancelClick={() => {
              setIsDeleteModalOpen(false);
              setDeleteItem(null);
            }}
          />
        )}
      </div>
    );
  }
);
